import { RefObject } from 'react';

import { REASON_FOR_UPDATING_USER } from './slices/auth/helpers';
import {
  AuthProviderOption,
  CompointsRatesMoney,
  CompPoints,
  User,
  UserWallet,
  Wallet,
} from '@/api/auth';
import {
  BonanzaPromoMenuDataApi,
  FortuneWheelMenuDataApi,
  StarWinnersApi,
} from '@/api/cms';
import { AboutUsApi } from '@/api/cms/aboutUs';
import { CMSImageBase } from '@/api/cms/base';
import { CMSGameTheme } from '@/api/cms/gameThemes';
import { LoyaltyPageApi } from '@/api/cms/loyalty';
import { OurBenefits } from '@/api/cms/ourBenefits';
import { ScratchGameMenuDataApi } from '@/api/cms/scratchGame';
import { ThirdPartySummary } from '@/api/cms/thirdPartySummaries';
import { Tournament } from '@/api/cms/tournaments';
import { VipApi } from '@/api/cms/vip';
import {
  Bonus,
  Currency,
  Freespin,
  ICountry,
  Jackpot,
  JackpotWinnner,
  Lootbox,
  PlayerFields,
  PlayerJackpotWin,
  PlayerSettings,
  PlayerTransaction,
  Rakebacks,
} from '@/api/common';
import { GamesFilter, GameV1, Provider, Real, RecentGame } from '@/api/games';
import { Limit } from '@/api/profile';
import { TopPlayer } from '@/api/tournaments';
import { TopSpotGameStatus, TopSpotStaticData } from '@/helpers/topSpotGame';
import { LivespinsSDK } from '@/modules/lobby/components/LiveSpins/types';
import { LoyaltyDataI } from '@/modules/loyalty/components/helpers';
import { BonanzaPromoStatus } from '@/modules/promotions';

export interface PlayerState {
  playerFields: PlayerFields | undefined;
  playerSettings: PlayerSettings | undefined;
  pendingPlayerSettings: boolean;
  lastSuccessfulPaymentsByCurrency: Record<string, number>;
  lastSuccessfulPaymentsByCurrencyPending: boolean;
  isAuthenticatedByCookie: boolean;
}

export enum License {
  CW = 'CW',
  EE = 'EE',
}

export interface TechInfoState {
  isMobileDevice: boolean;
  gameOpenBackUrl: string | null;
  pendingStore: boolean;
  serverError?: string | null;

  countries: ICountry[];
  countryCode: string | undefined;
  currencies: Currency[];
  enableCurrencies: Currency[];
  formatAmountByCurrency: (amount: number, currency: string) => number;
  currencyConversion: (amount: number, currency: string) => string;

  isAdditionalUserDataForm: boolean;
  toggleAdditionalUserDataForm: () => void;

  tabBarHeight: number;
  handleTabBarHeight: (ref: RefObject<HTMLDivElement>) => void;

  setCountryCodeData: (isEstonia: boolean) => void;

  authProvidersOptions: AuthProviderOption[];
  pendingAuthProvidersOptions: boolean;
}

interface AuthActions {
  fetchUser: (
    reasonForUpdatingUser: REASON_FOR_UPDATING_USER,
    isActiveSessionBackend?: boolean
  ) => Promise<void>;
  refreshUser: () => Promise<void>;
  insertUser: (
    user: User,
    reasonForUpdatingUser: REASON_FOR_UPDATING_USER
  ) => Promise<void>;
  updateUserReason: (reason: REASON_FOR_UPDATING_USER) => void;
  resetUser: () => Promise<void>;

  setUserWallets: (wallets: Wallet[]) => Promise<void>;
  changeUserWallet: (currency: Wallet['currency']) => Promise<void>;
  deleteUserWallet: (
    prevCurrency: Wallet['currency'],
    nextCurrency?: Wallet['currency']
  ) => Promise<void>;

  formatAmount: (amount: number, withSymbol?: boolean) => number | string;
  unFormatAmount: (amount: number) => number;
  setEmailConfirmationPopupSeen: () => void;
  setEmailConfirmationPopupHeight: (height: number) => void;

  getCompointsRatesMoney: () => Promise<void>;

  fetchCompoints: () => Promise<void>;
}

export type UserState = {
  user: User | undefined;
  reasonForUpdatingUser: REASON_FOR_UPDATING_USER | undefined;
  isAuthenticated: boolean;
  pendingUser: boolean;
  pendingUserPure: boolean;
  wallets: Wallet[];
  pendingWallets: boolean;

  afterLoginRedirectUrl: string | null;
  afterAdditionalDataRedirectUrl: string | null;
  afterRegistrationRedirectUrl: string | null;
  afterDepositRedirectUrl: string | null;

  compPoints: CompPoints | undefined;
  compPointsPending: boolean;

  compointsRatesMoney: CompointsRatesMoney[] | undefined;
  pendingCompointsRatesMoney: boolean;
};

interface AuthData extends UserState {
  extendedUserWallets: UserWallet[];
  currentWallet: UserWallet | null;
  isEmailConfirmationPopup: boolean;
  emailConfirmationPopupHeight: number;
  limits: Limit[];
}

export type AuthState = AuthData & AuthActions;

export interface GameCovers {
  [key: string]: { cover: CMSImageBase; gradientColor: string };
}

interface RegBanner {
  title: string;
  offer1: string[];
  offer2?: string;
  background?: string;
  character?: string;
}
export interface AddonsState {
  ourBenefits: OurBenefits | null;
  aboutUs: AboutUsApi | null;
  vip: VipApi | null;
  loyaltyData: LoyaltyPageApi | null;
  loyaltyPending: boolean;
  regBannerActionBtnRect: DOMRect | null;
  thirdPartySummaries: ThirdPartySummary[] | undefined;
  pendingThirdPartySummaries: boolean;
  availableCrossSell: boolean;

  currentCancelBonusData: { isBonusType: boolean; id: number } | undefined;

  getLoyaltyData: () => LoyaltyDataI | undefined;

  isVerifyMessage: boolean;
  isKnowYourLimit: boolean | undefined;
  afterRegStep: boolean | undefined;
  livespinsSDK: LivespinsSDK | null;

  regBanner: RegBanner | null;
  initialRegFormCurrency?: string | null;
  isNavigateOnCloseRegistration: boolean;
  isNDBFlow: boolean;
}

export interface BonanzaPromoState {
  userBonanzaGroup: BonanzaPromoStatus;
  userBonanzaGroupKey: string;
  bonanzaReward: string;
  bonanzaProgressPercent: number;
  bonanzaPromoMenuData: BonanzaPromoMenuDataApi | null;
  pendingBonanzaPromoMenuData: boolean;
  fetchBonanzaPromoMenuData: (locale: string) => void;
}

export interface TopSpotGameState {
  isSuperVip: boolean;
  userTarget: number;
  userTSGProgressPercentByGroup: number;
  userTSGProgressPercentByCP: number;
  topSpotGameStatus: TopSpotGameStatus | undefined;
  topSpotGameStaticData?: TopSpotStaticData;
  isTopSpotGameAvailable: boolean;
  isPassedSuperSpotBreakpoint: boolean;
}

interface GamesInfo {
  games: GameV1[];
  pagination: GamesFilter.Pagination;
}
export type GamesByParameterData = {
  [parameter: string]: GamesInfo;
};

type GamesFilterParam = Pick<GamesFilter.Body, 'pageSize' | 'page' | 'sort'> & {
  subName?: string; // to separate different lists with the same category or provider
  staticAmount?: boolean;
};

interface GamesData {
  providers: Provider[];
  pendingProviders: boolean;

  gameThemes: CMSGameTheme[];

  recentGames: GameV1[];
  pendingRecentGames: boolean;

  favoriteGames: GameV1[];
  favoriteGamesIds: number[];
  pendingFavoriteGames: boolean;

  gamesByCategories: GamesByParameterData;
  pendingGamesByCategories: { [category: string]: boolean };

  gamesByProviders: GamesByParameterData;
  pendingGamesByProviders: { [provider: string]: boolean };
  isLiveSpinsAvailable: boolean;
}

interface GamesActions {
  fetchProviders: () => Promise<void>;

  fetchGames: (
    body?: Omit<GamesFilter.Body, 'currencies'>,
    onError?: (error?: any) => void
  ) => Promise<GamesFilter.Response | undefined>;
  fetchGamesByIds: (
    body: GamesFilter.BodySelect,
    isMobileDevice: boolean
  ) => Promise<GamesFilter.ResponseSelect | undefined>;

  fetchRecentGames: (isMobileDevice: boolean) => Promise<void>;

  fetchFavoriteGames: () => Promise<void>;
  addFavoriteGame: (currencies: Real) => Promise<void>;
  removeFavoriteGame: (currency: Real) => Promise<void>;

  getGamesByCategory: (
    category: string,
    filter: GamesFilterParam
  ) => Promise<GamesInfo | undefined>;
  addGamesByCategory: (gamesInfo: GamesByParameterData) => void;
  togglePendingGamesByCategories: (category: string) => void;

  getGamesByProvider: (
    provider: string,
    filter: GamesFilterParam
  ) => Promise<GamesInfo | undefined>;
  addGamesByProvider: (gamesInfo: GamesByParameterData) => void;
  togglePendingGamesByProviders: (provider: string) => void;
}

export type GamesState = GamesData & GamesActions;

interface RealTimeServiceActions {
  setActiveJackpot: () => Promise<void>;
  fetchJackpotWinnersList: () => Promise<void>;
  addPlayerJackpotWin: (jackpotWin: PlayerJackpotWin) => void;

  fetchLootboxes: () => Promise<void>;
  updateLootboxes: (lootbox: Lootbox) => void;

  fetchBonuses: () => Promise<void>;
  updateBonuses: (bonus: Bonus) => void;

  fetchRakebacks: () => Promise<void>;

  fetchFreespins: () => Promise<void>;
  updateFreespins: (freespin: Freespin) => void;

  updatePlayerPayments: (payment: PlayerTransaction) => void;

  fetchRecentGamesIds: () => Promise<void>;
  updateRecentGames: (game: RecentGame) => void;
}
interface RealTimeServiceData {
  activeJackpot: Jackpot | undefined;
  playerJackpotWins: PlayerJackpotWin[];
  jackpotWinnersList: JackpotWinnner[];
  pendingJackpots: boolean;

  wheelLootboxes: Lootbox[];
  scratchLootboxes: Lootbox[];
  withoutTagsLootboxes: Lootbox[];
  pendingLootboxes: boolean;

  bonusesPending: boolean;
  bonuses: Bonus[];

  rakebacks: Rakebacks[];

  freespinsPending: boolean;
  freespins: Freespin[];

  playerPayments: PlayerTransaction[];
  recentGamesIds: string[];

  activeLootboxId: number | undefined;
}

export type RealTimeServiceState = RealTimeServiceActions & RealTimeServiceData;

export interface TournamentsState {
  tournaments: Tournament[];
  userTournamentsStatuses: TopPlayer[];
  tournamentGameCategories: string[];
  tournamentGameIds: string[];
  fetchUserTournamentStatus: () => Promise<void>;
  updateUserTournamentStatus: (newTournamentStatus: TopPlayer) => void;
}

export interface ScratchGameState {
  scratchGameMenuData: ScratchGameMenuDataApi | null;
  pendingScratchGameMenuData: boolean;
  fetchScratchGameMenuData: (locale: string) => Promise<void>;
}

export interface FortuneWheelState {
  fortuneWheelMenuData: FortuneWheelMenuDataApi | null;
  pendingFortuneWheelMenuData: boolean;
  fetchFortuneWheelMenuData: (locale: string) => Promise<void>;
}

export type CommonStoreType = TechInfoState &
  PlayerState &
  AuthState &
  AddonsState &
  RealTimeServiceState &
  GamesState &
  TournamentsState &
  TopSpotGameState &
  BonanzaPromoState &
  ScratchGameState &
  FortuneWheelState;
